import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import AuthLayout from '../components/layout/authlayout';
import Input from '../components/common/input';
import Button from '../components/common/button';
import Checkbox from '../components/common/checkbox';
import { loginUser } from '../store/actions/authActions';
import { signInSchema, validateWithZod } from '../validators/authValidator';
import { googleOAuthLogin } from '../services/authService';
import { showError } from '../utils/toast';
import { useToast } from '../components/ToastProvider' ;

const SignIn = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.auth);
  const { showToast } = useToast();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: true,
  });

  const [errors, setErrors] = useState({});
  const [googleLoading, setGoogleLoading] = useState(false);

  useEffect(() => {
    const showLogoutToast = localStorage.getItem('showLogoutToast');
    if (showLogoutToast === 'true') {
      localStorage.removeItem('showLogoutToast');
      setTimeout(() => {
        showToast('Logged out successfully', 'success');
      }, 500);
    }

    setGoogleLoading(false);
  }, [showToast]);


 
  useEffect(() => {
    const handlePageShow = () => {
      setGoogleLoading(false);
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const validation = validateWithZod(signInSchema, {
      email: formData.email,
      password: formData.password,
    });

    if (!validation.success) {
      setErrors(validation.errors);
      return null;
    }

    setErrors({});
    return validation.data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validatedData = validateForm();
    if (!validatedData) {
      return;
    }

    try {
      await dispatch(loginUser(
        {
          email: validatedData?.email,
          password: validatedData?.password,
        },
        formData?.rememberMe,
        navigate
      ));
    } catch (error) {
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setGoogleLoading(true);
      const response = await googleOAuthLogin();
      const authUrl = response?.data?.authUrl || response?.authUrl;

      if (authUrl) {
        // Redirect to Google OAuth
        window.location.href = authUrl;
      } else {
        throw new Error('Failed to get Google OAuth URL');
      }
    } catch (error) {
      setGoogleLoading(false);
      const errorMessage = error?.message || error?.data?.error || 'Failed to initiate Google sign in';
      showError(errorMessage);
    }
  };

  return (
    <AuthLayout
      title="Sign In To Your Account"
      subtitle="Enter your credentials to access your account"
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <Input
          label="Email address"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="abc@gmail.com"
          error={errors.email}
          required
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
            </svg>
          }
        />

        <Input
          label="Password"
          type="password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          placeholder="••••••••••••"
          error={errors.password}
          required
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
          }
        />

        <div className="flex items-center justify-between flex-wrap gap-3">
          <Checkbox
            label="Remember me"
            name="rememberMe"
            checked={formData.rememberMe}
            onChange={handleChange}
          />

          <Link
            to="/forgot-password"
            className="text-sm text-primary hover:text-primary-dark transition-colors font-medium hover:underline"
          >
            Forgot password?
          </Link>
        </div>

        <Button
          type="submit"
          fullWidth
          loading={loading}
          disabled={loading}
        >
          {loading ? 'Signing In...' : 'Sign In'}
        </Button>

        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">Or continue with</span>
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          fullWidth
          loading={googleLoading}
          disabled={googleLoading || loading}
          onClick={handleGoogleLogin}
          icon={
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
          }
        >
          {googleLoading ? 'Redirecting...' : 'Sign in with Google'}
        </Button>

        <p className="text-center text-sm text-gray-600 pt-2">
          Don't have an account?{' '}
          <Link
            to="/signup"
            className="text-primary hover:text-primary-dark font-semibold transition-colors hover:underline"
          >
            Sign up
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
};

export default SignIn;